import PortalTooltip from '@/components/PortalTooltip';
import { Button } from '@/components/ui/button';
import {
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { execute } from '@/graphql/execute';
import { LayerType, LayersQuery, Project } from '@/graphql/graphql';
import { usePageMeta } from '@/hooks/usePageMeta';
import PageSkeleton from '@/layout/Skeleton';
import { analytics } from '@/lib/analytics';
import { cn } from '@/lib/utils';
import { useAlert } from '@/providers/AlertProvider';
import { useDialog } from '@/providers/DialogProvider';
import {
  createLayerMutation,
  deleteLayerMutation,
  layerPropertiesQuery,
  layersQuery,
  updateLayerMutation,
} from '@/queries/layer.query';
import { ChevronRight, DotsVertical, EditOne, EyeSlash, LockSolid, Plus, Trash } from '@mynaui/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useFormik } from 'formik';
import { Loader2 } from 'lucide-react';
import { FunctionComponent, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Nestable, { RenderItem } from 'react-nestable';
import 'react-nestable/dist/styles/index.css';
import { useOutletContext, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { LayerIcon, useLayers } from './Viewer/Layers';

interface iProjectLayersProps {}

const buildTreeData = (layers: LayersQuery['layers']) => {
  const rootLayers = layers.filter((layer) => layer.parentLayerId === null);

  const getChildren = (id: string): LayersQuery['layers'] => {
    const children = layers.filter((layer) => layer.parentLayerId === id);

    return children.map((child) => {
      return {
        ...child,
        children: getChildren(child.id),
      };
    });
  };
  return rootLayers.map((layer) => {
    return {
      ...layer,
      children: getChildren(layer.id),
    };
  });
};

const ProjectLayers: FunctionComponent<iProjectLayersProps> = () => {
  const { projectId } = useParams();
  if (!projectId) throw new Error('Project ID is required');
  const { t } = useTranslation('project');
  const { openDialog } = useDialog();
  const { openAlert } = useAlert();
  const { project, projectLoading, href } = useOutletContext<{
    project: Project;
    projectLoading: boolean;
    projectError: Error | null;
    href: string;
  }>();
  usePageMeta({ title: [t('layers.title'), project?.name] });
  const client = useQueryClient();
  const {
    data: layersData,
    refetch,
    isLoading,
  } = useQuery({
    queryKey: ['layers', projectId],
    queryFn: async () => {
      const result = await execute(layersQuery, { projectId });
      return result;
    },
  });
  const { onTurnOffLayer } = useLayers({ projectId });

  const mutation = useMutation({
    mutationFn: async (variables: { id: string; parentLayerId?: string }) => {
      await execute(updateLayerMutation, {
        input: {
          id: variables.id,
          parentLayerId: variables.parentLayerId,
        },
      });

      return true;
    },

    onMutate: async (variables) => {
      await client.cancelQueries({ queryKey: ['layers', projectId] });

      const previousLayers = client.getQueryData(['layers', projectId]);

      const newLayers: any = JSON.parse(JSON.stringify(previousLayers));

      const index = newLayers.layers.findIndex((layer: any) => layer.id === variables.id);
      newLayers.layers[index].parentLayerId = variables.parentLayerId;

      client.setQueryData(['layers', projectId], newLayers);

      return { previousLayers, newLayers };
    },
    onError: (_, __, context) => {
      client.setQueryData(['layers', projectId], context?.previousLayers);
    },
    onSettled: () => {
      client.invalidateQueries({ queryKey: ['layers', projectId] });
    },
  });

  const layers = layersData?.layers ?? [];

  const _handleCreateLayer = () => openDialog(<LayerEditorContent projectId={projectId} onSuccess={refetch} />);
  const _handleEditLayer = (layer: Partial<LayersQuery['layers'][0]>) => {
    openDialog(<LayerEditorContent projectId={projectId} layer={layer} onSuccess={refetch} />);
  };

  const _handleToggleLock = async (layerId: string) => {
    const layer = layers.find((layer) => layer.id === layerId);
    if (!layer) return;

    await toast
      .promise(execute(updateLayerMutation, { input: { id: layerId, isLocked: !layer.isLocked } }), {
        loading: t('layers.updatingLayer'),
        success: t('layers.layerUpdated'),
        error: t('layers.failedUpdateLayer'),
      })
      .unwrap();
    refetch();
  };
  const _handleToggleHidden = async (layerId: string) => {
    const layer = layers.find((layer) => layer.id === layerId);
    if (!layer) return;

    if (!layer.isHidden) onTurnOffLayer(layer.id);

    await toast
      .promise(execute(updateLayerMutation, { input: { id: layerId, isHidden: !layer.isHidden } }), {
        loading: t('layers.updatingLayer'),
        success: t('layers.layerUpdated'),
        error: t('layers.failedUpdateLayer'),
      })
      .unwrap();
    refetch();
  };

  const _handleDeleteLayer = (layerId: string) => {
    openAlert({
      title: t('layers.deleteLayerTitle'),
      content: t('layers.deleteLayerDescription'),
      actions: [
        {
          label: t('layers.deleteLayer'),
          variant: 'destructive',
          onClick: () => _handleExecuteDeleteLayer(layerId),
        },
      ],
    });
  };

  const _handleExecuteDeleteLayer = async (layerId: string) => {
    const toastId = toast.loading(t('layers.deletingLayer'));
    try {
      await execute(deleteLayerMutation, { id: layerId });
      analytics.capture('Layer Deleted');
      toast.success(t('layers.layerDeleted'), { id: toastId });
      refetch();
    } catch (error) {
      toast.error(t('layers.failedDeleteLayer'), { id: toastId });
    }
  };

  const _handleChange = ({ items: newTree }: { items: any[] }) => {
    const flattenTree = (tree: any[]): any[] => {
      return tree.flatMap((item) => [
        item,
        ...flattenTree(item.children.map((child: any) => ({ ...child, parentLayerId: item.id }))),
      ]);
    };
    const flattenedTree = flattenTree(
      newTree.map((item) => ({ ...item, children: item.children || [], parentLayerId: null })),
    );

    const updatedLayers = flattenedTree.filter((item) => {
      const original = layers.find((layer) => layer.id === item.id);
      return original?.parentLayerId !== item.parentLayerId;
    });

    if (updatedLayers.length > 1) throw new Error('Multiple layers updated');
    const updatedLayer = updatedLayers[0];
    _handleUpdateParentLayerId(updatedLayer.id, updatedLayer.parentLayerId);
  };

  const _handleUpdateParentLayerId = async (layerId: string, parentLayerId: string) => {
    const toastId = toast.loading(t('layers.updatingLayer'));
    try {
      await mutation.mutateAsync({ id: layerId, parentLayerId });
      toast.success(t('layers.layerUpdated'), { id: toastId });
      refetch();
    } catch (error) {
      toast.error(t('layers.failedUpdateLayer'), { id: toastId });
    }
  };

  const checkIfLayerIsHidden = (layerId: string): boolean => {
    const layer = layers.find((layer) => layer.id === layerId);
    if (!layer) return false;
    if (layer.isHidden) return true;
    if (layer.parentLayerId) return checkIfLayerIsHidden(layer.parentLayerId);
    return false;
  };

  const renderItem: RenderItem = (props) => {
    const { item, collapseIcon } = props;
    const isHidden = checkIfLayerIsHidden(item.id);
    const isSelfHidden = item.isHidden;
    return (
      <div className='flex items-center gap-2 hover:bg-slate-100 w-full pl-2 p-1 rounded dark:hover:bg-stone-800/50'>
        {collapseIcon}
        <LayerIcon type={item.type} />
        <div className='flex items-center gap-1 flex-1'>
          {isHidden && (
            <EyeSlash
              data-tooltip-id={isSelfHidden ? 'layers-hidden-self' : 'layers-hidden'}
              className='size-3.5 stroke-2'
            />
          )}
          {item.isLocked && <LockSolid data-tooltip-id='layers-locked' className='size-3.5 stroke-2' />}
          <span className={cn('text-sm font-medium text-slate-900 flex-1 dark:text-white', isHidden && 'line-through')}>
            {item.name}
          </span>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant='ghost' size='icon' className='size-6 hover:bg-slate-200 rounded-md'>
              <DotsVertical className='size-6 stroke-2' />
              <span className='sr-only'>{t('layers.openMenu')}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className='w-56' align='start'>
            <DropdownMenuGroup>
              <DropdownMenuItem onSelect={() => _handleEditLayer(item)}>
                <EditOne className='size-4 stroke-2' />
                {t('layers.editLayer')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuCheckboxItem checked={item.isLocked} onCheckedChange={() => _handleToggleLock(item.id)}>
                {t('layers.lockLayer')}
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem checked={item.isHidden} onCheckedChange={() => _handleToggleHidden(item.id)}>
                {t('layers.disableLayer')}
              </DropdownMenuCheckboxItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem variant='destructive' onSelect={() => _handleDeleteLayer(item.id)}>
                <Trash className='size-4 stroke-2' />
                {t('layers.deleteLayer')}
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  };

  const renderCollapseIcon = useCallback((props: { isCollapsed: boolean }) => {
    return (
      <ChevronRight
        className={cn(
          'size-4 text-gray-700 dark:text-gray-300 stroke-2 transition-transform',
          !props.isCollapsed && 'rotate-90',
        )}
      />
    );
  }, []);

  return (
    <PageSkeleton
      steps={[
        { label: projectLoading ? t('edit.loading') : project.name, href },
        { label: t('layers.title'), href: `${href}/layers` },
      ]}
      actions={[
        {
          icon: <Plus />,
          onClick: _handleCreateLayer,
          tooltip: t('layers.createLayer'),
        },
      ]}>
      <PortalTooltip delayShow={120} id='layers-hidden-self'>
        {t('layers.hiddenSelf')}
      </PortalTooltip>
      <PortalTooltip delayShow={120} id='layers-hidden'>
        {t('layers.hiddenParent')}
      </PortalTooltip>
      <PortalTooltip delayShow={120} id='layers-locked'>
        {t('layers.locked')}
      </PortalTooltip>
      <div className='max-w-screen-md mx-auto w-full px-4 pt-4 flex-1 flex flex-col'>
        <div className='flex items-center justify-between mb-4'>
          <div className='border border-slate-200/70 rounded-md p-1 w-full bg-white dark:bg-stone-900 dark:border-stone-800'>
            {isLoading && (
              <div className='flex items-center px-2 py-1 h-full gap-1'>
                <Loader2 className='size-4 animate-spin' />
                <span className='text-sm text-slate-800 dark:text-white font-medium'>{t('layers.loadingLayers')}</span>
              </div>
            )}
            <Nestable
              onChange={_handleChange}
              className='w-full'
              items={buildTreeData(layers)}
              renderItem={renderItem}
              renderCollapseIcon={renderCollapseIcon}
              maxDepth={32}
            />
          </div>
        </div>
      </div>
    </PageSkeleton>
  );
};

export default ProjectLayers;

export const LayerEditorContent: FunctionComponent<{
  projectId: string;
  layer?: Partial<LayersQuery['layers'][0]>;
  onSuccess: () => void;
}> = ({ projectId, layer, onSuccess }) => {
  const { t } = useTranslation('project');
  const { data: layerPropertiesData } = useQuery({
    queryKey: ['layerProperties', layer?.id],
    queryFn: async () => {
      const result = await execute(layerPropertiesQuery, { layerId: layer?.id || '' });
      return result;
    },
  });
  const formik = useFormik<{
    name: string;
    description: string;
    parentLayerId: string | null;
    type: LayerType;
    propertyAliases: Array<{ property: string; alias: string }>;
  }>({
    initialValues: {
      name: layer?.name || '',
      description: layer?.description || '',
      parentLayerId: layer?.parentLayerId || null,
      type: layer?.type || 'GROUP',
      propertyAliases: layer?.propertyAliases || [],
    },
    onSubmit: async (values) => {
      if (layer) {
        await _handleUpdateLayer(values);
      } else {
        await _handleCreateLayer(values);
      }
    },
  });

  const _handleUpdateLayer = async (values: {
    name: string;
    description: string;
    parentLayerId: string | null;
    propertyAliases: Array<{ property: string; alias: string }>;
  }) => {
    if (!layer) return;
    const toastId = toast.loading(t('layers.updatingLayer'));
    try {
      await execute(updateLayerMutation, {
        input: {
          id: layer.id!,
          name: values.name,
          description: values.description,
          parentLayerId: values.parentLayerId,
          propertyAliases: values.propertyAliases,
        },
      });
      analytics.capture('Layer Updated');
      toast.success(t('layers.layerUpdated'), { id: toastId });
      onSuccess();
    } catch (error) {
      toast.error(t('layers.failedUpdateLayer'), { id: toastId });
    }
  };

  const _handleCreateLayer = async (values: {
    name: string;
    description: string;
    parentLayerId: string | null;
    type: LayerType;
    propertyAliases: Array<{ property: string; alias: string }>;
  }) => {
    const toastId = toast.loading(t('layers.creatingLayer'));
    try {
      await execute(createLayerMutation, {
        input: {
          name: values.name,
          description: values.description,
          parentLayerId: values.parentLayerId,
          type: values.type,
          projectId,
        },
      });
      analytics.capture('Layer Created', { type: values.type });
      toast.success(t('layers.layerCreated'), { id: toastId });
      onSuccess();
    } catch (error) {
      toast.error(t('layers.failedCreateLayer'), { id: toastId });
    }
  };

  const _handleAddPropertyAlias = () => {
    formik.setFieldValue('propertyAliases', [...formik.values.propertyAliases, { property: '', alias: '' }]);
  };

  const _handleRemovePropertyAlias = (index: number) => {
    const newAliases = formik.values.propertyAliases.filter((_, i) => i !== index);
    formik.setFieldValue('propertyAliases', newAliases);
  };

  const _handlePropertyAliasChange = (index: number, field: 'property' | 'alias', value: string) => {
    const newAliases = [...formik.values.propertyAliases];
    newAliases[index] = { ...newAliases[index], [field]: value };
    formik.setFieldValue('propertyAliases', newAliases);
  };

  return (
    <DialogContent className='sm:max-w-[520px]'>
      <DialogHeader>
        <DialogTitle>{layer ? t('layerEditor.editTitle') : t('layerEditor.createTitle')}</DialogTitle>
        <DialogDescription>
          {layer ? t('layerEditor.editDescription') : t('layerEditor.createDescription')}
        </DialogDescription>
      </DialogHeader>
      <div className='grid gap-4'>
        <div className='grid gap-3'>
          <Label htmlFor='name-1'>{t('layerEditor.name')}</Label>
          <Input
            id='name-1'
            name='name'
            value={formik.values.name}
            onChange={formik.handleChange}
            placeholder={t('layerEditor.namePlaceholder')}
          />
        </div>
        {!layer && (
          <div className='grid gap-3'>
            <Label htmlFor='type'>{t('layerEditor.type')}</Label>
            <Select
              defaultValue={formik.values.type}
              onValueChange={(value) => formik.setFieldValue('type', value as LayerType)}
              disabled={!!layer}>
              <SelectTrigger className='w-full'>
                <SelectValue placeholder={t('layerEditor.selectType')} />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>{t('layerEditor.type')}</SelectLabel>
                  <SelectItem value='GROUP'>{t('layerEditor.folder')}</SelectItem>
                  <SelectItem value='VECTOR'>{t('layerEditor.vector')}</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        )}
        <div className='grid gap-3'>
          <Label htmlFor='description-1'>{t('layerEditor.description')}</Label>
          <Textarea
            id='description-1'
            name='description'
            value={formik.values.description}
            onChange={formik.handleChange}
            placeholder={t('layerEditor.descriptionPlaceholder')}
          />
        </div>
        {['VECTOR'].includes(formik.values.type) && (
          <div className='grid gap-3'>
            <Label>{t('layerEditor.propertyAliases')}</Label>
            <div className='flex flex-col gap-2'>
              {formik.values.propertyAliases.map((alias, index) => (
                <div key={index} className='flex items-center gap-2'>
                  <Select
                    value={alias.property}
                    onValueChange={(value) => _handlePropertyAliasChange(index, 'property', value)}>
                    <SelectTrigger className='flex-1'>
                      <SelectValue placeholder={t('layerEditor.selectProperty')} />
                    </SelectTrigger>
                    <SelectContent>
                      {layerPropertiesData?.layerProperties?.map((prop: string) => (
                        <SelectItem key={prop} value={prop}>
                          {prop}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder={t('layerEditor.alias')}
                    value={alias.alias}
                    onChange={(e) => _handlePropertyAliasChange(index, 'alias', e.target.value)}
                    className='flex-1'
                  />
                  <Button
                    type='button'
                    variant='ghost'
                    size='icon'
                    onClick={() => _handleRemovePropertyAlias(index)}
                    className='size-9 shrink-0'>
                    <Trash className='size-4' />
                  </Button>
                </div>
              ))}
              <Button type='button' variant='outline' onClick={_handleAddPropertyAlias} className='w-full'>
                <Plus className='size-4 mr-2' />
                {t('layerEditor.addPropertyAlias')}
              </Button>
            </div>
          </div>
        )}
      </div>
      <DialogFooter>
        <DialogClose asChild>
          <Button variant='outline'>{t('layerEditor.cancel')}</Button>
        </DialogClose>
        <DialogClose asChild>
          <Button type='submit' onClick={formik.submitForm} disabled={formik.isSubmitting}>
            {t('layerEditor.saveChanges')}
          </Button>
        </DialogClose>
      </DialogFooter>
    </DialogContent>
  );
};

// export default ProjectLayersPage;
